import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { bookingsAPI, customerAPI, roomsAPI, staffAPI } from '../lib/api'
import type { Booking, Room, Customer } from '../types'
import { Button } from '../components/ui/Button'
import { Select } from '../components/ui/Select'
import { Input } from '../components/ui/Input'
import { Textarea } from '../components/ui/Textarea'
import { useToast } from '../components/ui/useToast'
import { useTenantSettings } from '../hooks/useTenantSettingsHook'
import {
  Plus, RefreshCw, Calendar as CalendarIcon, Clock, Users, MapPin,
  Edit, Trash2, CheckCircle, XCircle, AlertCircle, TrendingUp,
  DollarSign, Filter, Download, Repeat, Bell
} from 'lucide-react'

type StaffOption = { id: number; name: string }

export function BookingsPageNew() {
  const { notify } = useToast()
  const { settings } = useTenantSettings()

  const [rooms, setRooms] = useState<Room[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [bookings, setBookings] = useState<Booking[]>([])
  const [staffList, setStaffList] = useState<StaffOption[]>([])

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [filterRoomId, setFilterRoomId] = useState<number | ''>('')
  const [filterDateFrom, setFilterDateFrom] = useState<string>('')
  const [filterDateTo, setFilterDateTo] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('all')

  // UI State
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [recurringBooking, setRecurringBooking] = useState(false)
  const [recurrenceCount, setRecurrenceCount] = useState(1)
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<'daily' | 'weekly'>('weekly')

  const { register, handleSubmit, reset, watch } = useForm<{
    room_id: number
    customer_id: number
    staff_id?: number | ''
    date: string
    start: string
    end: string
    notes?: string
    status?: string
  }>()

  const watchRoom = watch('room_id')
  const watchDate = watch('date')
  const watchStart = watch('start')
  const watchEnd = watch('end')

  // Calculate suggested amount
  const suggestedAmount = (() => {
    try {
      if (!watchRoom || !watchDate || !watchStart || !watchEnd) return null
      const room = rooms.find(r => r.id === Number(watchRoom))
      const start = new Date(`${watchDate}T${watchStart}:00`)
      const end = new Date(`${watchDate}T${watchEnd}:00`)
      if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return null
      const hours = (end.getTime() - start.getTime()) / 36e5
      const rate = room?.hourly_rate ?? Number(settings?.default_hourly_rate)
      if (!rate || !Number.isFinite(rate)) return null
      return Math.round(hours * rate * 100) / 100
    } catch {
      return null
    }
  })()

  // Load initial data
  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        setLoading(true)
        const [rRes, cRes, sRes] = await Promise.all([
          roomsAPI.list(),
          customerAPI.getAll({ per_page: 100 }),
          staffAPI.list(),
        ])
        if (mounted) {
          if (rRes.success) setRooms(Array.isArray(rRes.data) ? (rRes.data as Room[]) : [])
          if (cRes.success) {
            const raw = cRes.data as any
            setCustomers(Array.isArray(raw) ? raw : (raw.items || []))
          }
          if (sRes.success) {
            const raw = sRes.data as any
            const list: StaffOption[] = Array.isArray(raw)
              ? raw.map((e: any) => ({
                  id: Number(e.id),
                  name: e.name || e.email || `Staff ${e.id}`
                }))
              : []
            setStaffList(list)
          }
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [])

  // Reload bookings with filters
  const reloadBookings = async () => {
    try {
      setLoading(true)
      const params: any = {}
      if (filterRoomId) params.room_id = filterRoomId
      if (filterDateFrom) params.from = new Date(filterDateFrom).toISOString()
      if (filterDateTo) params.to = new Date(filterDateTo).toISOString()
      const bRes = await bookingsAPI.list(params)
      if (bRes.success) setBookings(Array.isArray(bRes.data) ? bRes.data as Booking[] : [])
    } catch (e: any) {
      setError(e.message || 'Failed to load bookings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reloadBookings()
  }, [filterRoomId, filterDateFrom, filterDateTo])

  // Create booking(s)
  const onCreate = handleSubmit(async (values) => {
    setError(null)
    const start_time = new Date(`${values.date}T${values.start}:00`)
    const end_time = new Date(`${values.date}T${values.end}:00`)

    if (end_time <= start_time) {
      setError('End time must be after start time')
      notify({ kind: 'error', message: 'End time must be after start time' })
      return
    }

    try {
      setLoading(true)

      if (recurringBooking && recurrenceCount > 1) {
        // Create recurring bookings
        const bookingsToCreate = []
        for (let i = 0; i < recurrenceCount; i++) {
          const offset = recurrenceFrequency === 'daily' ? i : i * 7
          const bookingStart = new Date(start_time)
          bookingStart.setDate(bookingStart.getDate() + offset)
          const bookingEnd = new Date(end_time)
          bookingEnd.setDate(bookingEnd.getDate() + offset)

          bookingsToCreate.push({
            room_id: Number(values.room_id),
            customer_id: Number(values.customer_id),
            start_time: bookingStart.toISOString(),
            end_time: bookingEnd.toISOString(),
            notes: values.notes,
            total_amount: suggestedAmount ?? undefined,
            status: values.status || 'confirmed',
            staff_id: values.staff_id ? Number(values.staff_id) : null,
          })
        }

        // Create all bookings
        const results = await Promise.all(
          bookingsToCreate.map(b => bookingsAPI.create(b))
        )

        const successCount = results.filter(r => r.success).length
        if (successCount > 0) {
          await reloadBookings()
          reset()
          setRecurringBooking(false)
          setShowCreateForm(false)
          notify({ kind: 'success', message: `${successCount} booking(s) created successfully` })
        } else {
          notify({ kind: 'error', message: 'Failed to create bookings' })
        }
      } else {
        // Single booking
        const res = await bookingsAPI.create({
          room_id: Number(values.room_id),
          customer_id: Number(values.customer_id),
          start_time: start_time.toISOString(),
          end_time: end_time.toISOString(),
          notes: values.notes,
          total_amount: suggestedAmount ?? undefined,
          status: values.status || 'confirmed',
          staff_id: values.staff_id ? Number(values.staff_id) : null,
        })

        if (res.success) {
          await reloadBookings()
          reset()
          setShowCreateForm(false)
          notify({ kind: 'success', message: 'Booking created successfully' })
        } else {
          setError(res.message || 'Failed to create booking')
          notify({ kind: 'error', message: res.message || 'Failed to create booking' })
        }
      }
    } catch (e: any) {
      setError(e.message || 'Failed to create booking')
      notify({ kind: 'error', message: e.response?.status === 409 ? 'Booking conflict' : e.message })
    } finally {
      setLoading(false)
    }
  })

  // Delete booking
  const deleteBooking = async (id: number) => {
    if (!confirm('Are you sure you want to delete this booking?')) return
    try {
      await bookingsAPI.delete(id)
      await reloadBookings()
      notify({ kind: 'success', message: 'Booking deleted' })
    } catch (e: any) {
      notify({ kind: 'error', message: 'Failed to delete booking' })
    }
  }

  // Filter bookings by status
  const filteredBookings = bookings.filter(b => {
    if (filterStatus === 'all') return true
    return b.status === filterStatus
  })

  // Calculate stats
  const stats = {
    total: filteredBookings.length,
    confirmed: filteredBookings.filter(b => b.status === 'confirmed').length,
    pending: filteredBookings.filter(b => b.status === 'pending').length,
    cancelled: filteredBookings.filter(b => b.status === 'cancelled').length,
    revenue: filteredBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Bookings Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage room bookings and reservations</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={reloadBookings} disabled={loading} className="flex items-center gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            New Booking
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/5 dark:to-indigo-500/5 border-blue-200 dark:border-blue-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Bookings</p>
                <p className="text-3xl font-bold mt-2 text-blue-900 dark:text-blue-100">{stats.total}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
                <CalendarIcon className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-500/5 dark:to-emerald-500/5 border-green-200 dark:border-green-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 dark:text-green-400">Confirmed</p>
                <p className="text-3xl font-bold mt-2 text-green-900 dark:text-green-100">{stats.confirmed}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-500/5 dark:to-orange-500/5 border-yellow-200 dark:border-yellow-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Pending</p>
                <p className="text-3xl font-bold mt-2 text-yellow-900 dark:text-yellow-100">{stats.pending}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500">
                <Clock className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-500/5 dark:to-pink-500/5 border-red-200 dark:border-red-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-red-600 dark:text-red-400">Cancelled</p>
                <p className="text-3xl font-bold mt-2 text-red-900 dark:text-red-100">{stats.cancelled}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-red-500 to-pink-500">
                <XCircle className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-500/5 dark:to-pink-500/5 border-purple-200 dark:border-purple-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Total Revenue</p>
                <p className="text-2xl font-bold mt-2 text-purple-900 dark:text-purple-100">£{(stats.revenue / 100).toFixed(2)}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Booking Form */}
      {showCreateForm && (
        <div className="card border-2 border-orange-200 dark:border-orange-800">
          <div className="card-header flex items-center justify-between bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10">
            <div className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-orange-600" />
              <h2 className="font-semibold text-lg">Create New Booking</h2>
            </div>
            <Button variant="outline" onClick={() => {setShowCreateForm(false); reset()}} className="text-sm">
              Cancel
            </Button>
          </div>
          <div className="card-body">
            <form onSubmit={onCreate} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    Room *
                  </label>
                  <Select {...register('room_id', { required: true, valueAsNumber: true })}>
                    <option value="">Select a room</option>
                    {rooms.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="form-label flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-500" />
                    Customer *
                  </label>
                  <Select {...register('customer_id', { required: true, valueAsNumber: true })}>
                    <option value="">Select a customer</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <label className="form-label flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-gray-500" />
                    Date *
                  </label>
                  <Input type="date" {...register('date', { required: true })} />
                </div>
                <div>
                  <label className="form-label flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    Start Time *
                  </label>
                  <Input type="time" step="900" {...register('start', { required: true })} />
                </div>
                <div>
                  <label className="form-label flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    End Time *
                  </label>
                  <Input type="time" step="900" {...register('end', { required: true })} />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Assign Staff (optional)</label>
                  <Select {...register('staff_id')}>
                    <option value="">Unassigned</option>
                    {staffList.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="form-label">Status</label>
                  <Select {...register('status')}>
                    <option value="confirmed">Confirmed</option>
                    <option value="pending">Pending</option>
                  </Select>
                </div>
              </div>

              <div>
                <label className="form-label">Notes (optional)</label>
                <Textarea placeholder="Optional booking notes..." {...register('notes')} rows={3} />
              </div>

              {/* Recurring Booking Option */}
              <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={recurringBooking}
                    onChange={(e) => setRecurringBooking(e.target.checked)}
                    className="rounded"
                  />
                  <Repeat className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium">Recurring Booking</span>
                </label>

                {recurringBooking && (
                  <div className="grid md:grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="form-label text-sm">Frequency</label>
                      <Select value={recurrenceFrequency} onChange={(e) => setRecurrenceFrequency(e.target.value as any)}>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                      </Select>
                    </div>
                    <div>
                      <label className="form-label text-sm">Number of Occurrences</label>
                      <Input
                        type="number"
                        min="1"
                        max="52"
                        value={recurrenceCount}
                        onChange={(e) => setRecurrenceCount(Number(e.target.value))}
                      />
                    </div>
                  </div>
                )}
              </div>

              {suggestedAmount != null && (
                <div className="p-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    Suggested amount: <span className="font-bold">£{suggestedAmount.toFixed(2)}</span>
                    {recurringBooking && recurrenceCount > 1 && (
                      <span className="ml-2">(£{(suggestedAmount * recurrenceCount).toFixed(2)} total for {recurrenceCount} bookings)</span>
                    )}
                  </p>
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                  <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-800">
                <Button type="button" variant="outline" onClick={() => {setShowCreateForm(false); reset()}}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {loading ? 'Creating...' : (recurringBooking && recurrenceCount > 1 ? `Create ${recurrenceCount} Bookings` : 'Create Booking')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters & Bookings List */}
      <div className="card">
        <div className="card-header flex items-center gap-2">
          <Filter className="h-5 w-5 text-orange-600" />
          <h2 className="font-semibold text-lg">Filter & Search</h2>
        </div>
        <div className="card-body">
          <div className="grid md:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="form-label text-sm">Room</label>
              <Select value={String(filterRoomId)} onChange={(e) => setFilterRoomId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">All rooms</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="form-label text-sm">From Date</label>
              <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="form-label text-sm">To Date</label>
              <Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />
            </div>
            <div>
              <label className="form-label text-sm">Status</label>
              <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">All Status</option>
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </div>
          </div>

          {/* Bookings List */}
          {loading && filteredBookings.length === 0 ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl animate-pulse">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                </div>
              ))}
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="h-16 w-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No Bookings Found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">Create your first booking to get started</p>
              <Button onClick={() => setShowCreateForm(true)} className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                <Plus className="h-4 w-4 mr-2" />
                Create Booking
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredBookings.map(b => {
                const room = rooms.find(r => r.id === b.room_id)
                const customer = customers.find(c => c.id === b.customer_id)
                const staff = staffList.find(s => s.id === b.staff_id)
                const start = new Date(b.start_time)
                const end = new Date(b.end_time)
                const duration = ((end.getTime() - start.getTime()) / 36e5).toFixed(1)

                return (
                  <div
                    key={b.id}
                    className="p-4 rounded-xl border-2 border-gray-200 dark:border-gray-800 hover:border-orange-500 dark:hover:border-orange-500 transition-all"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`p-2 rounded-lg ${
                            b.status === 'confirmed' ? 'bg-green-100 dark:bg-green-500/10' :
                            b.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-500/10' :
                            'bg-red-100 dark:bg-red-500/10'
                          }`}>
                            {b.status === 'confirmed' ? <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" /> :
                             b.status === 'pending' ? <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" /> :
                             <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />}
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{room?.name || 'Unknown Room'}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{customer?.name || 'Unknown Customer'}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                            <CalendarIcon className="h-3 w-3" />
                            {start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                            <Clock className="h-3 w-3" />
                            {start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - {end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          {staff && (
                            <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                              <Users className="h-3 w-3" />
                              {staff.name}
                            </div>
                          )}
                          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                            <TrendingUp className="h-3 w-3" />
                            {duration}h · £{((b.total_amount || 0) / 100).toFixed(2)}
                          </div>
                        </div>

                        {b.notes && (
                          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2 italic">{b.notes}</p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => deleteBooking(b.id)}
                          className="text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
